/**
 * Co-Op Battle View
 *
 * React component wrapping CoOpCombatRenderer for live co-op combat.
 * Connects to PvE socket for combat state updates and renders the battle.
 */

import React, { useRef, useEffect, useState } from 'react';
import { CoOpCombatRenderer } from './CoOpCombatRenderer';
import type { AxialCoordinate } from '@drawn-of-war/shared';

interface CoOpBattleViewProps {
  matchId: string;
  currentUserId: string;
  battlefieldConfig: {
    width: number;
    height: number;
    obstacles?: AxialCoordinate[];
  };
  /** Initial creatures to render */
  creatures?: Array<{
    instanceId: string;
    name: string;
    position: AxialCoordinate;
    teamId: string;
    ownerId: string;
    health: number;
    maxHealth: number;
    spriteData?: unknown;
  }>;
  onBattleComplete?: () => void;
}

export const CoOpBattleView: React.FC<CoOpBattleViewProps> = ({
  matchId,
  currentUserId,
  battlefieldConfig,
  creatures,
  onBattleComplete: _onBattleComplete,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<CoOpCombatRenderer | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize renderer
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth || 800;
    const height = container.clientHeight || 600;

    const renderer = new CoOpCombatRenderer({
      canvasWidth: width,
      canvasHeight: height,
      battlefieldWidth: battlefieldConfig.width,
      battlefieldHeight: battlefieldConfig.height,
      currentUserId,
      obstacles: battlefieldConfig.obstacles,
    });

    rendererRef.current = renderer;

    renderer.init().then((canvas) => {
      if (canvas && container) {
        container.appendChild(canvas);
      }

      // Render obstacles if any
      if (battlefieldConfig.obstacles) {
        renderer.renderObstacles(battlefieldConfig.obstacles);
      }

      setIsInitialized(true);
    });

    return () => {
      renderer.destroy();
      rendererRef.current = null;
      setIsInitialized(false);
    };
  }, [matchId, currentUserId, battlefieldConfig]);

  // Render initial creatures when initialized
  useEffect(() => {
    if (!isInitialized || !rendererRef.current || !creatures) return;

    const renderer = rendererRef.current;
    for (const creature of creatures) {
      const playerId = creature.teamId === 'team2' ? 'player2' : 'player1';
      renderer.setTeamColorOverride(creature.instanceId, creature.teamId, creature.ownerId);
      renderer.renderCreatureWithId(
        creature.instanceId,
        creature.position,
        creature.name,
        playerId as 'player1' | 'player2',
      );
      renderer.applyTeamColorOverride(creature.instanceId);
    }
  }, [isInitialized, creatures]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        minHeight: '400px',
        position: 'relative',
        background: '#1a1a2e',
      }}
    />
  );
};
